import { tyreListQuerySchema } from '@chukua/common';
import { Button } from 'antd';
import { CircleDot, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { DirectoryTable } from '../../../components/DirectoryTable';
import { useDirectoryList } from '../../../hooks/use-directory-list';
import { buildTyreColumns } from '../components/build-tyre-columns';
import { TyreFormDrawer } from '../components/TyreFormDrawer';
import { useTyreFilterOptions } from '../hooks/use-tyre-filter-options';
import { useTyreSave } from '../hooks/use-tyre-save';
import { listTyres } from '../tyres.api';
import { useCanManageTyres } from '../hooks/use-can-manage-tyres';

/** The tyre register (gitaumoses4/chukua#177): every tyre by serial, where it sits and how worn it is. Paged, searched, filtered and sorted on the server. */
export function TyresPage() {
  const { state, list, isRefreshing, reload } = useDirectoryList(tyreListQuerySchema, listTyres);
  const save = useTyreSave(reload);
  const canManage = useCanManageTyres();
  const { brands, sizes } = useTyreFilterOptions();
  const [isRegistering, setIsRegistering] = useState(false);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const newButton = canManage && (
    <Button
      type="primary"
      icon={<Plus className="size-4" />}
      onClick={() => {
        save.resetError();
        setIsRegistering(true);
      }}
    >
      Register tyre
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {newButton && <div className="flex justify-end">{newButton}</div>}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load tyres" description="Something went wrong loading the register. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading tyres" withSearch />}
      {state.status === 'loaded' && (
        <DirectoryTable
          page={state.page}
          columns={buildTyreColumns({ list, brands, sizes })}
          list={list}
          isRefreshing={isRefreshing}
          newButton={newButton}
          emptyIcon={CircleDot}
          emptyTitle="No tyres registered yet"
          emptyDescription="Register each tyre by its serial number to follow where it is fitted, how worn it is and what it cost."
          searchPlaceholder="Search serial or brand"
          noMatchTitle="No matching tyres"
        />
      )}
      <TyreFormDrawer open={isRegistering} error={save.error} submitting={save.saving} onSubmit={save.register} onClose={() => setIsRegistering(false)} />
    </div>
  );
}
